import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Menu from '../main/menu';
import './styles/LinkedInPoster.css';
import SystemManager from './system/SystemManager';
import LinkedInService from './system/LinkedInService';
import { auth } from '../firebase';
import { db } from '../firebase';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { testTelegramConnection, testWorkflow } from './system/TestConnection';
import { Link } from 'react-router-dom';
import TelegramService from './system/TelegramService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

// ThemeContext til at håndtere tema-ændringer
export const ThemeContext = React.createContext({
  isDarkMode: true,
  toggleTheme: () => {}
});

const LinkedInPoster = ({ onLogout }) => {
  const { userType } = useAuth(); // Hent brugertype fra AuthContext
  const { t } = useLanguage(); // Brug sprogkonteksten
  const [telegramConnected, setTelegramConnected] = useState(false);
  const [isConnectedToLinkedIn, setIsConnectedToLinkedIn] = useState(false);
  const [isConnectedToTelegram, setIsConnectedToTelegram] = useState(false);
  const [telegramUsername, setTelegramUsername] = useState(null);
  const [connectionCode, setConnectionCode] = useState(null);
  const [showConnectionInstructions, setShowConnectionInstructions] = useState(false);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [tokenExpiry, setTokenExpiry] = useState(null);
  const [linkedInTokenData, setLinkedInTokenData] = useState(null);
  const [linkedInStatus, setLinkedInStatus] = useState({
    isConnected: false,
    profile: null,
    loading: true
  });
  const [telegramDeliveryPreference, setTelegramDeliveryPreference] = useState('website');
  const [isSettingPreference, setIsSettingPreference] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark-theme');
  const navigate = useNavigate();
  const location = useLocation();

  // Lyt efter tema-ændringer fra andre komponenter
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'theme') {
        setTheme(e.newValue || 'dark-theme');
        document.body.className = e.newValue || 'dark-theme';
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Check og anvend det aktuelle tema ved indlæsning
    const currentTheme = localStorage.getItem('theme') || 'dark-theme';
    document.body.className = currentTheme;
    setTheme(currentTheme);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const initSystem = async () => {
      if (auth.currentUser) {
        await SystemManager.initializeSystem(auth.currentUser.uid);
      }
    };

    initSystem();
  }, []);

  useEffect(() => {
    const checkConnections = async () => {
      if (!auth.currentUser) return;
  
      try {
        const userId = auth.currentUser.uid;
        setIsLoadingProfile(true);
        
        // Tjek Telegram-forbindelse
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists() && userDoc.data().telegramChatId) {
          setIsConnectedToTelegram(true);
          setTelegramUsername(userDoc.data().telegramUsername || 'Unavngivet');
        }
        
        // Tjek LinkedIn-forbindelse
        const linkedInToken = await LinkedInService.getLinkedInToken(userId);
        if (linkedInToken && linkedInToken.accessToken) {
          setIsConnectedToLinkedIn(true);
          setLinkedInTokenData(linkedInToken);
          
          if (linkedInToken.expiresAt) {
            const expiryDate = new Date(linkedInToken.expiresAt);
            const now = new Date();
            const daysRemaining = Math.max(0, Math.floor((expiryDate - now) / (1000 * 60 * 60 * 24)));
            setTokenExpiry(daysRemaining);
          }
        }

        // Hent brugerens leveringspræference
        const prefDoc = await getDoc(doc(db, 'users', userId, 'settings', 'post_preferences'));
        if (prefDoc.exists()) {
          setTelegramDeliveryPreference(prefDoc.data().postDeliveryMethod || 'website');
        }
      } catch (error) {
        console.error('Fejl ved kontrol af forbindelser:', error);
        setError(t('linkedInPoster', 'messages.connectionError'));
      } finally {
        setIsLoadingProfile(false);
      }
    };
    
    checkConnections();
  }, [auth.currentUser]);

  useEffect(() => {
    if (!auth.currentUser) return;

    // Lyt efter ændringer i brugerens dokument
    const unsubscribe = onSnapshot(doc(db, 'users', auth.currentUser.uid), (doc) => {
      if (doc.exists()) {
        setTelegramConnected(doc.data().telegramConnected || false);
        
        // Hvis Telegram er blevet tilknyttet, fjern instruktionerne
        if (doc.data().telegramConnected) {
          setShowConnectionInstructions(false);
          setConnectionCode(null);
        }
      }
    });

    // Cleanup listener når komponenten unmountes
    return () => unsubscribe();
  }, [auth.currentUser]);

  // Hent LinkedIn status
  useEffect(() => {
    const checkLinkedInStatus = async () => {
      if (!auth.currentUser) return;

      try {
        console.log("Henter LinkedIn data for bruger:", auth.currentUser.uid);
        const tokenData = await LinkedInService.getLinkedInToken(auth.currentUser.uid);
        console.log("Hentet LinkedIn data:", tokenData);
        
        if (tokenData) {
          console.log("LinkedIn forbindelse fundet");
          
          // Beregn dage til token udløber hvis expiresIn er tilgængelig
          let expiryDays = null;
          if (tokenData.expiresIn) {
            const expiryDate = new Date(
              new Date(tokenData.updatedAt || Date.now()).getTime() + tokenData.expiresIn * 1000
            );
            const now = new Date();
            expiryDays = Math.max(0, Math.floor((expiryDate - now) / (1000 * 60 * 60 * 24)));
            setTokenExpiry(expiryDays);
            console.log("Token udløber om", expiryDays, "dage");
          }

          // Kontroller om vi har profildata
          if (tokenData.profile) {
            console.log("Profildata fundet:", tokenData.profile);
          } else {
            console.warn("Profildata mangler i token data");
          }

          setLinkedInStatus({
            isConnected: true,
            profile: tokenData.profile || null,
            loading: false
          });
          
          // Opdater connected status også
          setIsConnectedToLinkedIn(true);
          setLinkedInTokenData(tokenData);
        } else {
          console.log("Ingen LinkedIn forbindelse fundet");
          setLinkedInStatus({
            isConnected: false,
            profile: null,
            loading: false
          });
          setIsConnectedToLinkedIn(false);
          setLinkedInTokenData(null);
        }
      } catch (error) {
        console.error('Fejl ved tjek af LinkedIn status:', error);
        setLinkedInStatus({
          isConnected: false,
          profile: null,
          loading: false
        });
        setIsConnectedToLinkedIn(false);
        setLinkedInTokenData(null);
        setError(t('linkedInPoster', 'messages.connectionError'));
      }
    };

    checkLinkedInStatus();
  }, [auth.currentUser]);

  const generateConnectionCode = async () => {
    if (!auth.currentUser) {
      console.error("Ingen bruger logget ind");
      return;
    }

    setIsGeneratingCode(true);
    console.log("Genererer tilknytningskode for bruger:", auth.currentUser.uid);
    
    try {
      // Generer en unik kode
      const code = 'NUVIK-' + Math.random().toString(36).substring(2, 8).toUpperCase();
      console.log("Genereret kode:", code);
      
      // Gem koden i Firestore
      await setDoc(doc(db, 'connection_codes', code), {
        userId: auth.currentUser.uid,
        createdAt: new Date(),
        expires: new Date(Date.now() + 15 * 60000) // Udløber om 15 minutter
      });

      console.log("Kode gemt i Firestore");
      setConnectionCode(code);
      setShowConnectionInstructions(true);
    } catch (error) {
      console.error("Fejl ved generering af kode:", error);
      setError(t('linkedInPoster', 'messages.codeGenerationError'));
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const handleTelegramConnectClick = () => {
    console.log("Tilknyt Telegram knap klikket");
    generateConnectionCode();
  };

  const handleConnectLinkedIn = () => {
    LinkedInService.redirectToLinkedIn();
  };

  const handleTestWorkflow = async () => {
    try {
      await testWorkflow();
      alert(t('linkedInPoster', 'messages.workflowTestSuccess'));
    } catch (error) {
      console.error('Fejl ved test af workflow:', error);
      alert(t('linkedInPoster', 'messages.workflowTestError'));
    }
  };

  const handleDisconnectLinkedIn = async () => {
    if (!auth.currentUser) return;
    
    try {
      setDisconnecting(true);
      const userId = auth.currentUser.uid;
      console.log("Starter afkobling af LinkedIn for bruger:", userId);
      
      const success = await LinkedInService.disconnectLinkedIn(userId);
      
      if (success) {
        console.log("LinkedIn afkobling gennemført succesfuldt");
        setIsConnectedToLinkedIn(false);
        setLinkedInTokenData(null);
        setTokenExpiry(null);
        setLinkedInStatus({
          isConnected: false,
          profile: null,
          loading: false
        });
        setSuccessMessage('Din LinkedIn-konto er nu afkoblet');
        
        // Genindlæs siden efter kort forsinkelse for at sikre data er opdateret
        setTimeout(() => {
          console.log("Genindlæser siden...");
          window.location.reload();
        }, 1500);
      } else {
        console.error("LinkedIn afkobling fejlede");
        setError(t('linkedInPoster', 'messages.linkedInDisconnectError', { message: error.message }));
      }
    } catch (error) {
      console.error('Fejl ved afkobling af LinkedIn:', error);
      setError(t('linkedInPoster', 'messages.linkedInDisconnectError', { message: error.message }));
    } finally {
      setDisconnecting(false);
    }
  };

  const handleDisconnectTelegram = async () => {
    try {
      setIsConnecting(true); // Brug isConnecting state til at vise loading-tilstand
      setError(null);
      
      // Check om brugeren er logget ind
      if (!auth.currentUser) {
        setError(t('linkedInPoster', 'messages.telegramDisconnectError', { message: 'Du skal være logget ind for at frakoble Telegram' }));
        setIsConnecting(false);
        return;
      }
      
      console.log("Starter afkobling af Telegram for bruger:", auth.currentUser.uid);
      
      // Hent brugerens ID token for at sikre korrekt auth
      const idToken = await auth.currentUser.getIdToken(true);
      
      // Hent funktioner fra Firebase
      const functions = getFunctions();
      
      // Sæt region hvis nødvendigt (fjern kommentar hvis din funktion er i en specifik region)
      // const functions = getFunctions(undefined, 'us-central1');
      
      const disconnectTelegramFunction = httpsCallable(functions, 'disconnectTelegram');
      
      // Kald cloud function for at frakoble Telegram med brugerens ID token
      const result = await disconnectTelegramFunction({ idToken });
      
      if (result.data && result.data.success) {
        console.log("Telegram afkobling gennemført succesfuldt");
        setIsConnectedToTelegram(false);
        setTelegramUsername(null);
        setSuccessMessage('Din Telegram-konto er nu afkoblet');
        
        // Genindlæs siden efter kort forsinkelse for at sikre data er opdateret
        setTimeout(() => {
          console.log("Genindlæser siden...");
          window.location.reload();
        }, 1500);
      } else {
        console.error("Telegram afkobling fejlede:", result.data);
        setError(t('linkedInPoster', 'messages.telegramDisconnectError', { message: result.data ? result.data.error : 'Ukendt fejl' }));
      }
    } catch (error) {
      console.error('Fejl ved afkobling af Telegram:', error);
      setError(t('linkedInPoster', 'messages.telegramDisconnectError', { message: error.message || 'Ukendt fejl' }));
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDeliveryPreferenceChange = async (preference) => {
    if (!auth.currentUser) return;
    
    try {
      setIsSettingPreference(true);
      const userId = auth.currentUser.uid;
      console.log("Starter opsætning af leveringspræference for bruger:", userId);
      
      const success = await LinkedInService.setTelegramDeliveryPreference(userId, preference);
      
      if (success) {
        console.log("Leveringspræference opsætning gennemført succesfuldt");
        setTelegramDeliveryPreference(preference);
        setSuccessMessage('Din leveringspræference er nu opdateret');
        
        // Genindlæs siden efter kort forsinkelse for at sikre data er opdateret
        setTimeout(() => {
          console.log("Genindlæser siden...");
          window.location.reload();
        }, 1500);
      } else {
        console.error("Leveringspræference opsætning fejlede");
        setError(t('linkedInPoster', 'messages.deliveryPreferenceError', { message: 'Kunne ikke opdatere leveringspræference' }));
      }
    } catch (error) {
      console.error('Fejl ved opsætning af leveringspræference:', error);
      setError(t('linkedInPoster', 'messages.deliveryPreferenceError', { message: error.message }));
    } finally {
      setIsSettingPreference(false);
    }
  };

  const navigateToPosts = () => {
    navigate('/linkedin-posts');
  };

  // Funktion til at håndtere temaskift direkte fra denne komponent
  const toggleTheme = () => {
    const newTheme = theme === 'light-theme' ? 'dark-theme' : 'light-theme';
    setTheme(newTheme);
    document.body.className = newTheme;
    localStorage.setItem('theme', newTheme);
    
    // Udløs en storage-event for at andre komponenter også kan reagere
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'theme',
      newValue: newTheme,
      oldValue: theme
    }));
  };

  if (isLoadingProfile) {
    return (
      <div className="lnps-loading">
        <div className="lnps-spinner"></div>
        <p>{t('settings', 'linkedin.loading')}</p>
      </div>
    );
  }

  // Brug ThemeContext.Provider til at dele tema-tilstand med underkomponenter
  return (
    <ThemeContext.Provider value={{ isDarkMode: theme === 'dark-theme', toggleTheme }}>
      <div className={`dashboard-layout ${theme}`}>
        <Menu onLogout={onLogout} />
        
        <main className="dashboard-content">
          <div className="lnps-header">
            <h1 className="lnps-header-title">
              {userType === 'customer' 
                ? t('settings', 'header.title') 
                : t('settings', 'header.title')}
            </h1>
            <p className="lnps-header-description">
              {userType === 'customer' 
                ? t('settings', 'header.description') 
                : t('settings', 'header.descriptionEmployee')}
            </p>
          </div>
          
          {successMessage && (
            <div className="lnps-message success">
              <div className="lnps-message-content">
                <div className="lnps-message-icon">
                  <i className="fas fa-check"></i>
                </div>
                {successMessage}
              </div>
              <button onClick={() => setSuccessMessage(null)} className="lnps-message-close">
                <i className="fas fa-times"></i>
              </button>
            </div>
          )}
          
          {error && (
            <div className="lnps-message error">
              <div className="lnps-message-content">
                <div className="lnps-message-icon">
                  <i className="fas fa-exclamation"></i>
                </div>
                {error}
              </div>
              <button onClick={() => setError(null)} className="lnps-message-close">
                <i className="fas fa-times"></i>
              </button>
            </div>
          )}
          
          <div className="lnps-container">
            {/* Sprog-sektion */}
            <div className="lnps-card lnps-service-card">
              <div className="lnps-card-inner">
                <div className="lnps-service-icon language">
                  <i className="fas fa-language"></i>
                </div>
                <div className="lnps-card-content">
                  <h3 className="lnps-card-title">{t('settings', 'language.title')}</h3>
                  <p className="lnps-card-description">{t('settings', 'language.description')}</p>
                  <div className="lnps-language-selector-container">
                    <LanguageSelector />
                  </div>
                </div>
              </div>
            </div>

            <div className="lnps-card lnps-service-card">
              <div className="lnps-card-inner">
                <div className="lnps-service-icon linkedin">
                  <i className="fab fa-linkedin"></i>
                </div>
                <div className="lnps-card-content">
                  <h3 className="lnps-card-title">{t('settings', 'linkedin.title')}</h3>
                  
                  {linkedInStatus.loading ? (
                    <p>{t('settings', 'linkedin.loading')}</p>
                  ) : linkedInStatus.isConnected ? (
                    <>
                      <p className="lnps-profile-connected">
                        <i className="fas fa-check-circle"></i> {t('settings', 'linkedin.connected')}
                      </p>
                      {linkedInStatus.profile ? (
                        <div className="lnps-profile-box">
                          <p className="lnps-profile-name">{linkedInStatus.profile.name || t('settings', 'linkedin.profileInfo.unnamed')}</p>
                          {linkedInStatus.profile.email && (
                            <p className="lnps-profile-email">{linkedInStatus.profile.email}</p>
                          )}
                          {/* Hvis vi har et sub felt, vis det som et ID */}
                          {linkedInStatus.profile.sub && (
                            <p className="lnps-profile-id">ID: {linkedInStatus.profile.sub}</p>
                          )}
                          <div className="lnps-token-info">
                            <p>
                              <span className="lnps-token-label">{t('settings', 'linkedin.profileInfo.status')}</span>
                              <span className="lnps-token-value">{t('settings', 'linkedin.profileInfo.active')}</span>
                            </p>
                            <p>
                              <span className="lnps-token-label">{t('settings', 'linkedin.profileInfo.expires')}</span>
                              <span className="lnps-token-value">
                                {tokenExpiry 
                                  ? t('settings', 'linkedin.profileInfo.expiresIn', { days: tokenExpiry }) 
                                  : t('settings', 'linkedin.profileInfo.unknown')}
                              </span>
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="lnps-status-card">
                          <div className="lnps-status-icon warning">
                            <i className="fas fa-exclamation-triangle"></i>
                          </div>
                          <div className="lnps-status-text">
                            {t('settings', 'linkedin.profileInfo.noProfileData')}
                          </div>
                        </div>
                      )}
                      <div className="lnps-button-group">
                        <button 
                          className="lnps-button lnps-button-danger"
                          onClick={handleDisconnectLinkedIn}
                          disabled={disconnecting}
                        >
                          {disconnecting ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-unlink"></i>} 
                          {t('settings', 'linkedin.disconnectButton')}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="lnps-card-description">{t('settings', 'linkedin.description')}</p>
                      <button 
                        className="lnps-button lnps-button-primary"
                        onClick={handleConnectLinkedIn}
                        disabled={isConnecting}
                      >
                        {isConnecting ? <i className="fas fa-spinner fa-spin"></i> : <i className="fab fa-linkedin"></i>} 
                        {t('settings', 'linkedin.connectButton')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
                
            {/* Kun vis Telegram og andre kort hvis brugeren ikke er kunde */}
            {userType !== 'customer' && (
              <>
                <div className="lnps-card lnps-service-card">
                  <div className="lnps-card-inner">
                    <div className="lnps-service-icon telegram">
                      <i className="fab fa-telegram"></i>
                    </div>
                    <div className="lnps-card-content">
                      <h3 className="lnps-card-title">{t('settings', 'telegram.title')}</h3>
                      {!telegramConnected ? (
                        <>
                          <p className="lnps-card-description">{t('settings', 'telegram.description')}</p>
                          <button 
                            className="lnps-button lnps-button-primary"
                            onClick={handleTelegramConnectClick}
                            disabled={isGeneratingCode}
                          >
                            {isGeneratingCode ? (
                              <>
                                <i className="fas fa-spinner fa-spin"></i> {t('settings', 'telegram.generating')}
                              </>
                            ) : (
                              <>
                                <i className="fab fa-telegram"></i> {t('settings', 'telegram.connectButton')}
                              </>
                            )}
                          </button>
                          {showConnectionInstructions && connectionCode && (
                            <div className="lnps-connection-guide">
                              <h4 className="lnps-guide-title">{t('settings', 'telegram.connectionGuide.title')}</h4>
                              <ol className="lnps-guide-steps">
                                <li className="lnps-guide-step">{t('settings', 'telegram.connectionGuide.steps.0')}</li>
                                <li className="lnps-guide-step">{t('settings', 'telegram.connectionGuide.steps.1')}</li>
                                <li className="lnps-guide-step">{t('settings', 'telegram.connectionGuide.steps.2')} <span className="lnps-code">{connectionCode}</span></li>
                              </ol>
                              <p className="lnps-guide-note">
                                <i className="fas fa-clock"></i> {t('settings', 'telegram.connectionGuide.expires')}
                              </p>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="lnps-profile-connected">
                            <i className="fas fa-check-circle"></i> {t('settings', 'telegram.connected')}
                          </p>
                          <div className="lnps-button-group">
                            <button 
                              className="lnps-button lnps-button-danger"
                              onClick={handleDisconnectTelegram}
                              disabled={isConnecting}
                            >
                              {isConnecting ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-unlink"></i>} 
                              {t('settings', 'telegram.disconnectButton')}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Leveringspræference sektion som selvstændigt kort */}
                {isConnectedToTelegram && (
                  <div className="lnps-card lnps-service-card">
                    <div className="lnps-card-inner">
                      <div className="lnps-service-icon settings">
                        <i className="fas fa-cog"></i>
                      </div>
                      <div className="lnps-card-content">
                        <h3 className="lnps-card-title">{t('settings', 'deliverySettings.title')}</h3>
                        <p className="lnps-card-description">{t('settings', 'deliverySettings.description')}</p>
                        
                        <div className="lnps-delivery-options">
                          <div className="lnps-toggle-grid">
                            <div className="lnps-toggle-option">
                              <input 
                                type="radio" 
                                id="delivery-website" 
                                name="delivery-preference" 
                                value="website"
                                checked={telegramDeliveryPreference === 'website'}
                                onChange={() => handleDeliveryPreferenceChange('website')}
                                disabled={isSettingPreference}
                              />
                              <label className="lnps-toggle-label" htmlFor="delivery-website">
                                <div className="lnps-toggle-icon">
                                  <i className="fas fa-desktop"></i>
                                </div>
                                <div className="lnps-toggle-text">{t('settings', 'deliverySettings.options.website')}</div>
                              </label>
                            </div>
                            
                            <div className="lnps-toggle-option">
                              <input 
                                type="radio" 
                                id="delivery-telegram" 
                                name="delivery-preference" 
                                value="telegram"
                                checked={telegramDeliveryPreference === 'telegram'}
                                onChange={() => handleDeliveryPreferenceChange('telegram')}
                                disabled={isSettingPreference}
                              />
                              <label className="lnps-toggle-label" htmlFor="delivery-telegram">
                                <div className="lnps-toggle-icon">
                                  <i className="fab fa-telegram-plane"></i>
                                </div>
                                <div className="lnps-toggle-text">{t('settings', 'deliverySettings.options.telegram')}</div>
                              </label>
                            </div>
                          </div>
                          
                          <p className="lnps-toggle-help">
                            {telegramDeliveryPreference === 'website' 
                              ? t('settings', 'deliverySettings.helpText.website')
                              : t('settings', 'deliverySettings.helpText.telegram')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {isConnectedToLinkedIn && telegramConnected && (
                  <div className="lnps-card lnps-success-card">
                    <div className="lnps-card-inner">
                      <div className="lnps-success-icon">
                        <i className="fas fa-check-circle"></i>
                      </div>
                      <div className="lnps-success-content">
                        <h3 className="lnps-success-title">{t('settings', 'readyCard.title')}</h3>
                        <p className="lnps-success-description">{t('settings', 'readyCard.description')}</p>
                        <button 
                          className="lnps-button lnps-button-secondary"
                          onClick={navigateToPosts}
                        >
                          <i className="far fa-list-alt"></i> {t('settings', 'readyCard.viewPostsButton')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            
            {/* Vis en specifik informationsbesked kun for kunder */}
            {userType === 'customer' && isConnectedToLinkedIn && (
              <div className="lnps-card lnps-success-card">
                <div className="lnps-card-inner">
                  <div className="lnps-success-icon">
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <div className="lnps-success-content">
                    <h3 className="lnps-success-title">{t('settings', 'customerSuccess.title')}</h3>
                    <p className="lnps-success-description">
                      {t('settings', 'customerSuccess.description')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </ThemeContext.Provider>
  );
};

export default LinkedInPoster; 